import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { Employee, EmployeeFormData } from '../types/employee.types';
import * as employeeService from '../services/employee.service';

type EmployeesState = {
  employees: Employee[];
  selectedEmployee: Employee | null;
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  limit: number;
};

type EmployeesAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_EMPLOYEES'; payload: { employees: Employee[]; total: number } }
  | { type: 'SET_SELECTED_EMPLOYEE'; payload: Employee | null }
  | { type: 'ADD_EMPLOYEE'; payload: Employee }
  | { type: 'UPDATE_EMPLOYEE'; payload: Employee }
  | { type: 'REMOVE_EMPLOYEE'; payload: string }
  | { type: 'SET_PAGINATION'; payload: { page: number; limit: number } };

type EmployeesContextType = {
  state: EmployeesState;
  fetchEmployees: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    employeeType?: string;
  }) => Promise<void>;
  fetchEmployeeById: (id: string) => Promise<void>;
  createEmployee: (data: EmployeeFormData) => Promise<Employee>;
  updateEmployee: (id: string, data: Partial<EmployeeFormData>) => Promise<Employee>;
  deleteEmployee: (id: string) => Promise<void>;
  updateEmployeeStatus: (id: string, status: 'ACTIVE' | 'INACTIVE' | 'ON_LEAVE' | 'TERMINATED') => Promise<void>;
  setSelectedEmployee: (employee: Employee | null) => void;
  setPagination: (page: number, limit: number) => void;
};

const initialState: EmployeesState = {
  employees: [],
  selectedEmployee: null,
  loading: false,
  error: null,
  total: 0,
  page: 1,
  limit: 10,
};

export const EmployeesContext = createContext<EmployeesContextType | undefined>(undefined);

function employeesReducer(state: EmployeesState, action: EmployeesAction): EmployeesState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_EMPLOYEES':
      return { ...state, employees: action.payload.employees, total: action.payload.total, loading: false };
    case 'SET_SELECTED_EMPLOYEE':
      return { ...state, selectedEmployee: action.payload };
    case 'ADD_EMPLOYEE':
      return { ...state, employees: [action.payload, ...state.employees], total: state.total + 1 };
    case 'UPDATE_EMPLOYEE':
      return {
        ...state,
        employees: state.employees.map(emp =>
          emp.id === action.payload.id ? action.payload : emp
        ),
        selectedEmployee:
          state.selectedEmployee?.id === action.payload.id ? action.payload : state.selectedEmployee,
      };
    case 'REMOVE_EMPLOYEE':
      return {
        ...state,
        employees: state.employees.filter(emp => emp.id !== action.payload),
        total: state.total - 1,
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        page: action.payload.page,
        limit: action.payload.limit,
      };
    default:
      return state;
  }
}

export const EmployeesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(employeesReducer, initialState);

  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  const setError = (error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  };

  const fetchEmployees = useCallback(
    async (params?: {
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
      employeeType?: string;
    }) => {
      setLoading(true);
      try {
        const page = params?.page ?? state.page;
        const limit = params?.limit ?? state.limit;
        const search = params?.search;
        const status = params?.status;
        const employeeType = params?.employeeType;
        const response = await employeeService.fetchEmployees({
          page,
          limit,
          search,
          status,
          employeeType: employeeType || undefined, // Only include if it has a value
        });
        dispatch({
          type: 'SET_EMPLOYEES',
          payload: { employees: response.data, total: response.total },
        });
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to fetch employees');
      }
    },
    []
  );

  const fetchEmployeeById = async (id: string) => {
    setLoading(true);
    try {
      const employee = await employeeService.fetchEmployeeById(id);
      dispatch({ type: 'SET_SELECTED_EMPLOYEE', payload: employee });
      setLoading(false);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch employee');
      setLoading(false);
    }
  };

  const createEmployee = async (data: EmployeeFormData): Promise<Employee> => {
    setLoading(true);
    try {
      const newEmployee = await employeeService.createEmployee(data);
      dispatch({ type: 'ADD_EMPLOYEE', payload: newEmployee });
      return newEmployee;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create employee';
      setError(message);
      throw new Error(message);
    }
  };

  const updateEmployee = async (id: string, data: Partial<EmployeeFormData>): Promise<Employee> => {
    setLoading(true);
    try {
      const updatedEmployee = await employeeService.updateEmployee(id, data);
      dispatch({ type: 'UPDATE_EMPLOYEE', payload: updatedEmployee });
      return updatedEmployee;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update employee';
      setError(message);
      throw new Error(message);
    }
  };

  const deleteEmployee = async (id: string) => {
    setLoading(true);
    try {
      await employeeService.deleteEmployee(id);
      dispatch({ type: 'REMOVE_EMPLOYEE', payload: id });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to delete employee');
      throw error;
    }
  };

  const updateEmployeeStatus = async (id: string, status: 'ACTIVE' | 'INACTIVE' | 'ON_LEAVE' | 'TERMINATED') => {
    setLoading(true);
    try {
      const updatedEmployee = await employeeService.updateEmployeeStatus(id, status);
      dispatch({ type: 'UPDATE_EMPLOYEE', payload: updatedEmployee });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update employee status');
      throw error;
    }
  };

  const setSelectedEmployee = (employee: Employee | null) => {
    dispatch({ type: 'SET_SELECTED_EMPLOYEE', payload: employee });
  };

  const setPagination = (page: number, limit: number) => {
    dispatch({ type: 'SET_PAGINATION', payload: { page, limit } });
  };

  return (
    <EmployeesContext.Provider
      value={{
        state,
        fetchEmployees,
        fetchEmployeeById,
        createEmployee,
        updateEmployee,
        deleteEmployee,
        updateEmployeeStatus,
        setSelectedEmployee,
        setPagination,
      }}
    >
      {children}
    </EmployeesContext.Provider>
  );
};

export const useEmployees = (): EmployeesContextType => {
  const context = useContext(EmployeesContext);
  if (context === undefined) {
    throw new Error('useEmployees must be used within an EmployeesProvider');
  }
  return context;
};

